import { useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = useMemo(() => comics.find((c) => c.id === Number(id)), [id]);

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((c) => c.id !== comic.id && c.category === comic.category)
      .slice(0, 6);
  }, [comic]);

  const categoryName = useMemo(() => {
    const map: Record<string, string> = { guoman: '国漫', riman: '日漫', hanman: '韩漫' };
    return comic ? map[comic.category] || comic.category : '';
  }, [comic]);

  useEffect(() => {
    if (comic) {
      document.title = `${comic.title} - 琉璃漫站 | 正版漫画在线阅读`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', `${comic.title}，${comic.author}作品。${comic.description.substring(0, 120)}。琉璃漫站正版授权，高清流畅阅读。`);
    }
  }, [comic]);

  if (!comic) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <p className="text-foreground-600">漫画不存在</p>
      </div>
    );
  }

  const chapters = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    num: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    date: new Date(new Date(comic.updatedAt).getTime() - i * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  }));

  const comicSchema = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: comic.title,
    author: { '@type': 'Person', name: comic.author },
    description: comic.description,
    image: comic.cover,
    url: `https://liulicomic.com/comic/${comic.id}`,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: '10',
      ratingCount: Math.floor(comic.views / 100),
    },
    genre: comic.subcategory,
    inLanguage: 'zh-CN',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://liulicomic.com/' },
      { '@type': 'ListItem', position: 2, name: categoryName, item: `https://liulicomic.com/category/${comic.category}` },
      { '@type': 'ListItem', position: 3, name: comic.title },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(comicSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <main className="min-h-screen pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: categoryName, path: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          {/* Comic Info */}
          <article className="flex flex-col md:flex-row gap-8 md:gap-12 mb-16">
            {/* Cover */}
            <div className="flex-shrink-0 w-full md:w-[300px]">
              <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-background-200" data-product-shop>
                <img
                  src={comic.cover}
                  alt={comic.title}
                  title={`${comic.title} - 琉璃漫站正版漫画`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Details */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-sm font-medium">
                  {categoryName}
                </span>
                <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-sm font-medium">
                  {comic.subcategory}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  comic.status === '连载中'
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-foreground-100 text-foreground-700'
                }`}>
                  {comic.status}
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                <div>
                  <p className="text-xs text-foreground-500 mb-1">作者</p>
                  <p className="text-sm font-medium text-foreground-900">{comic.author}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-1">评分</p>
                  <p className="text-sm font-medium text-accent-600 flex items-center gap-1">
                    <i className="ri-star-fill text-sm"></i>
                    {comic.rating}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-1">阅读量</p>
                  <p className="text-sm font-medium text-foreground-900">
                    {(comic.views / 10000).toFixed(0)}万
                  </p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-1">总章节</p>
                  <p className="text-sm font-medium text-foreground-900">{comic.chapters}话</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-1">最近更新</p>
                  <p className="text-sm font-medium text-foreground-900">{comic.updatedAt}</p>
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-6">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-lg bg-secondary-100 text-secondary-700 text-xs font-medium">
                    {tag}
                  </span>
                ))}
              </div>

              <p className="text-foreground-700 leading-relaxed text-sm mb-8">
                {comic.description}
              </p>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <button className="px-8 py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap">
                  <i className="ri-play-circle-line mr-2"></i>
                  开始阅读第1话
                </button>
                <button className="px-8 py-3 rounded-lg bg-background-100 text-foreground-700 font-semibold text-sm hover:bg-background-200 transition-all duration-200 whitespace-nowrap">
                  <i className="ri-bookmark-line mr-2"></i>
                  加入书架
                </button>
                <button className="px-8 py-3 rounded-lg bg-background-100 text-foreground-700 font-semibold text-sm hover:bg-background-200 transition-all duration-200 whitespace-nowrap">
                  <i className="ri-share-forward-line mr-2"></i>
                  分享
                </button>
              </div>
            </div>
          </article>

          {/* Chapter List */}
          <section className="mb-16">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                章节列表
              </h2>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 rounded-lg bg-background-100 text-foreground-600 text-sm hover:bg-background-200 transition-colors whitespace-nowrap">
                  正序
                </button>
                <button className="px-3 py-1.5 rounded-lg bg-foreground-900 text-background-50 text-sm whitespace-nowrap">
                  倒序
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {chapters.map((ch) => (
                <a
                  key={ch.num}
                  href="#"
                  className="flex items-center justify-between p-4 rounded-xl bg-background-100 hover:bg-background-200 transition-colors group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xs font-mono text-foreground-400 whitespace-nowrap">
                      CH.{ch.num}
                    </span>
                    <span className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                      {ch.title}
                    </span>
                  </div>
                  <span className="text-xs text-foreground-400 whitespace-nowrap ml-3">{ch.date}</span>
                </a>
              ))}
            </div>

            {comic.chapters > 20 && (
              <div className="text-center mt-6">
                <button className="px-8 py-3 rounded-lg border border-background-200 text-foreground-600 text-sm hover:bg-background-100 transition-colors whitespace-nowrap">
                  查看全部 {comic.chapters} 话
                </button>
              </div>
            )}
          </section>

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <section className="pb-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5">
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.id}
                    to={`/comic/${rc.id}`}
                    className="group"
                    data-product-shop
                  >
                    <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-background-200 mb-3">
                      <img
                        src={rc.cover}
                        alt={rc.title}
                        title={`${rc.title} - 琉璃漫站正版漫画`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <h3 className="font-heading font-bold text-foreground-900 text-sm line-clamp-1">
                      {rc.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">{rc.author}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  );
}